import type { GetResponsePriceOverrideCount, PriceOverrideStatus } from "@classes-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { format } from "date-fns";
import { computed, type Ref, unref } from "vue";
import { PRICE_OVERRIDE_API_DATE_FORMAT } from "@/constants.ts";
import { learningServiceRequest } from "@/services/utils";

export const QUERY_KEY = "fetchPriceOverridesCount";

export const useGetPriceOverridesCount = <TData = AxiosResponse<GetResponsePriceOverrideCount>>(
  status?: Ref<PriceOverrideStatus>,
  session?: Ref<Array<string>>,
  city?: Ref<Array<number>>,
  libraryId?: Ref<Array<string>>,
  overrideReasonId?: Ref<Array<string>>,
  orderStatus?: Ref<Array<string>>,
  decisionDateStart?: Ref<string | Date | undefined>,
  decisionDateEnd?: Ref<string | Date | undefined>,
  q?: Ref<string | undefined>,
  getArchived?: Ref<boolean> | boolean,
  useQueryOptions?: Partial<UseQueryOptions<AxiosResponse<GetResponsePriceOverrideCount>, Error, TData>>,
) => {
  const fetchPriceOverridesCount = async (): Promise<AxiosResponse<GetResponsePriceOverrideCount>> => {
    return learningServiceRequest({
      method: "get",
      url: "/price_overrides/count/",
      params: {
        status: unref(status),
        session: unref(session),
        city: unref(city),
        library_id: unref(libraryId),
        override_reason_id: unref(overrideReasonId),
        order_status: unref(orderStatus),
        decision_date_start:
          decisionDateStart?.value instanceof Date
            ? format(decisionDateStart?.value, PRICE_OVERRIDE_API_DATE_FORMAT)
            : unref(decisionDateStart),
        decision_date_end:
          decisionDateEnd?.value instanceof Date
            ? format(decisionDateEnd?.value, PRICE_OVERRIDE_API_DATE_FORMAT)
            : unref(decisionDateEnd),
        q: unref(q),
        get_archived: unref(getArchived),
      },
    });
  };

  return useQuery<AxiosResponse<GetResponsePriceOverrideCount>, Error, TData>({
    queryKey: computed(() => [
      QUERY_KEY,
      status,
      session,
      city,
      libraryId,
      overrideReasonId,
      orderStatus,
      decisionDateStart,
      decisionDateEnd,
      q,
    ]),
    queryFn: fetchPriceOverridesCount,
    ...useQueryOptions,
  });
};
